// Property Sauce SPA — hash router + auth gate.
// Routes: #/map (home), #/login, #/dashboard, #/property/:id, #/shared/:token,
// #/add?url=… — the map is the homepage and is PUBLIC: it renders the LINZ
// parcel/hazard/valuation layers and the geosearch bar for signed-out
// visitors, and only needs a session to show or create tracked properties
// (docs/plans/map-first-ux.md).
// First visit auto-starts a guest (anonymous) session so the app is usable
// before login; guests can do everything client-side (create/notes/share/map).
// Backend /api/* features are login-only (server rejects anonymous tokens).
import './style.css';
import { auth, signInAnonymously, onAuthStateChanged, wasSignedOut, clearSignedOutFlag, isGuest } from './lib/firebase.js';
import { renderLogin } from './views/login.js';
import { renderDashboard } from './views/dashboard.js';
import { renderShared } from './views/shared.js';
import { renderAdd } from './views/add.js';
import { renderAccount } from './views/account.js';
import { renderAccountShare } from './views/account_share.js';
// map is lazy — maplibre + pmtiles are ~1MB and shouldn't load on first paint.
const loadMap = () => import('./views/map.js').then((m) => m.renderMap);

// Side-effect: broadcasts the property list to the browser extension so its
// overlay buttons know which listings are already imported (no auth needed
// on the extension side — it just listens to this page).
import './lib/extension-sync.js';

const appEl = document.getElementById('app');

// iOS Safari ignores user-scalable=no (accessibility) — pinch still zooms the
// whole page there. gesture* events are iOS-only and don't touch MapLibre's
// pointer handlers, so blocking them kills page zoom without breaking the
// map's own pinch-zoom (see UX.md).
['gesturestart', 'gesturechange', 'gestureend'].forEach((ev) =>
  document.addEventListener(ev, (e) => e.preventDefault()),
);

/** Parse '#/property/abc?x=1' → { name:'property', params:['abc'], query:{x:'1'} } */
export function parseHash(hash = location.hash) {
  const raw = hash.replace(/^#\/?/, '');
  const [path, qs = ''] = raw.split('?');
  const parts = path.split('/').filter(Boolean);
  const query = Object.fromEntries(new URLSearchParams(qs));
  return { name: parts[0] || 'map', params: parts.slice(1), query };
}

export function navigate(hash) {
  location.hash = hash;
}

// Route values are loaders (async view modules may code-split on first use).
const routes = {
  login: () => renderLogin,
  dashboard: () => renderDashboard,
  add: () => renderAdd,
  // Single shared view (docs/plans/map-property-ux.md): the map is the
  // property canvas — old #/property/:id links redirect to the map with the
  // sheet open on that property.
  property: () => (el, { params, navigate }) => {
    navigate(`#/map${params[0] ? '?pid=' + params[0] : ''}`);
    return null;
  },
  shared: () => renderShared,
  account: () => renderAccount,
  'account-share': () => renderAccountShare,
  map: loadMap,
};

// Views that render without ANY session. The map qualifies: its data (LINZ
// parcels, council hazard/valuation tiles) is public and its property pins
// need a session — main.js passes a null user and the map degrades to
// browse-only.
const publicRoutes = new Set(['map', 'login', 'shared', 'account-share']);

// ── route-aware scaffolds: mirror the real page so the load feels like the
// actual app (map hero, gallery, list rows) instead of two generic panels. ──
const skHeader = () => `
  <div class="sk-header">
    <div class="sk-brand shimmer"></div>
    <div class="sk-nav shimmer"></div>
    <div class="sk-spacer"></div>
    <div class="sk-chip shimmer"></div>
  </div>`;
const skLoading = () =>
  '<div class="sk-loading"><span class="spinner"></span><span>Loading…</span></div>';
const skNotes = () => `
  <div class="sk-panel tall">
    <div class="sk-title shimmer"></div>
    <div class="sk-line shimmer"></div>
    <div class="sk-line shimmer"></div>
    <div class="sk-line short shimmer"></div>
  </div>`;

const SKELETONS = {
  property: () => `
  <div id="skeleton" aria-hidden="true">
    ${skHeader()}
    <div class="sk-body">
      <div class="sk-map shimmer"></div>
      <div class="sk-panel">
        <div class="sk-gallery">${[0, 1, 2, 3].map(() => '<div class="g shimmer"></div>').join('')}</div>
        <div class="sk-title shimmer" style="margin-top:14px"></div>
        <div class="sk-line short shimmer"></div>
        <div class="sk-pills">${[0, 1, 2, 3, 4].map(() => '<div class="p shimmer"></div>').join('')}</div>
      </div>
      ${skNotes()}
    </div>
    ${skLoading()}
  </div>`,
  dashboard: () => `
  <div id="skeleton" aria-hidden="true">
    ${skHeader()}
    <div class="sk-body">
      <div class="sk-title shimmer"></div>
      <div class="sk-panel" style="padding:8px 20px">
        ${[0, 1, 2, 3].map(() => `
        <div class="sk-row">
          <div class="sk-thumb shimmer"></div>
          <div style="flex:1">
            <div class="sk-line short shimmer"></div>
            <div class="sk-line tiny shimmer"></div>
          </div>
          <div class="sk-chip shimmer"></div>
        </div>`).join('')}
      </div>
    </div>
    ${skLoading()}
  </div>`,
  add: () => `
  <div id="skeleton" aria-hidden="true">
    ${skHeader()}
    <div class="sk-body">
      <div class="sk-map shimmer"></div>
      <div class="sk-panel">
        <div class="sk-title shimmer"></div>
        <div class="sk-line shimmer"></div>
        <div class="sk-chip wide shimmer"></div>
      </div>
    </div>
    ${skLoading()}
  </div>`,
  map: () => `
  <div id="skeleton" aria-hidden="true">
    ${skHeader()}
    <div class="sk-map-full shimmer"></div>
  </div>`,
  shared: () => `
  <div id="skeleton" aria-hidden="true">
    ${skHeader()}
    <div class="sk-body">
      <div class="sk-map shimmer"></div>
      <div class="sk-panel">
        <div class="sk-title shimmer"></div>
        <div class="sk-line short shimmer"></div>
      </div>
      ${skNotes()}
    </div>
    ${skLoading()}
  </div>`,
  'account-share': () => `
  <div id="skeleton" aria-hidden="true">
    ${skHeader()}
    <div class="sk-body">
      <div class="sk-panel">
        <div class="sk-title shimmer"></div>
        <div class="sk-line short shimmer"></div>
      </div>
    </div>
    ${skLoading()}
  </div>`,
  // fallback: the server's generic two-panel default
  default: null,
};

function renderSkeleton(name) {
  const build = SKELETONS[name] || SKELETONS.default;
  if (!build) return; // keep the server-rendered default
  appEl.innerHTML = build();
}

const state = { user: null, authReady: false };
let cleanup = null;

async function render() {
  if (cleanup) {
    try { cleanup(); } catch { /* snapshot unsub failures are non-fatal */ }
    cleanup = null;
  }
  const { name, params, query } = parseHash();
  const load = routes[name];
  if (!load) {
    navigate('#/map');
    return;
  }
  if (name === 'map' && !state.authReady) {
    // The map is public, but it must not mount before auth resolves: the
    // first-visit guest sign-in would land right after and re-render the
    // whole view (map torn down and rebuilt mid-load). Keep the map skeleton
    // instead — it renders in ~300ms.
    renderSkeleton(name);
    return;
  }
  if (!publicRoutes.has(name) && !state.authReady) {
    // Auth is still resolving — keep a route-shaped scaffold instead of
    // flashing the login screen at logged-in users on deep links.
    renderSkeleton(name);
    return;
  }
  if (!publicRoutes.has(name) && !state.user) {
    return renderLogin(appEl, { navigate, query });
  }
  const view = await load();
  cleanup = view(appEl, { user: state.user, params, query, navigate }) || null;
}

onAuthStateChanged(auth, (user) => {
  if (!user && wasSignedOut()) {
    // Real user signed out (this session or a previous one) — show the login
    // screen instead of minting a fresh guest (whose empty dashboard would
    // look like lost data).
    state.user = null;
    state.authReady = true;
    render();
    return;
  }
  if (!user) {
    // First visit (or expired session): start a guest session. Anonymous
    // sign-in is a single REST call (~100ms); rules treat guests as signed-in.
    signInAnonymously(auth).catch((e) => {
      console.warn('anonymous sign-in failed', e);
      state.user = null;
      state.authReady = true;
      render(); // login screen with retry
    });
    return;
  }
  if (!isGuest(user)) clearSignedOutFlag(); // real sign-in resets the flag
  state.user = user;
  state.authReady = true;
  render();
});

// Route changes also happen without auth changes.
window.addEventListener('hashchange', render);
render();
